import React, { ReactNode } from 'react';
import styled from 'styled-components';

const CheckboxContainer = styled.div<{
  $inline?: boolean;
  $disabled: boolean;
  $textAlignTop: boolean;
}>`
  display: ${props => (props.$inline ? 'inline-flex' : 'flex')};
  align-items: ${({ $textAlignTop }) =>
    $textAlignTop ? 'flex-start' : 'center'};
  cursor: pointer;
  padding-right: 8px;
  opacity: ${({ $disabled }) => ($disabled ? '0.4' : 'initial')};
`;

const Icon = styled.svg`
  fill: none;
  stroke: var(--black);
  stroke-width: 2px;
`;
// Hide checkbox visually but remain accessible to screen readers.
// Source: https://polished.js.org/docs/#hidevisually
// input.attrs({ type: 'checkbox' })
const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div<{
  checked: boolean;
  disabled: boolean;
  $textAlignTop: boolean;
}>`
  flex-shrink: 0;
  display: inline-block;
  width: 16px;
  height: 16px;
  background: ${props => (props.checked ? 'var(--green5)' : 'var(--black)')};
  ${props => (props.checked ? '' : 'border: 1px solid var(--gray4b);')};
  border-radius: 2px;
  transition: all 150ms;

  ${({ $textAlignTop }) => ($textAlignTop ? 'margin-top: 4px;' : '')}

  ${CheckboxContainer}:hover & {
    ${props =>
      props.checked || props.disabled
        ? ''
        : 'box-shadow: 0 0 5px 1px var(--green2b);'};
  }
  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 5px 1px var(--green2b);
  }

  ${Icon} {
    visibility: ${props => (props.checked ? 'visible' : 'hidden')};
  }

  ${HiddenCheckbox}:disabled + & {
    cursor: not-allowed;
  }
`;

const LabelWrap = styled.div`
  display: inline-block;
  margin-left: 8px;
`;

type IProps = {
  className?: string;
  onChange: (state: boolean) => void;
  checked: boolean;
  inline?: boolean;
  textAlignTop?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  inputAttrs?: {
    ref?: React.Ref<HTMLInputElement>;
    title?: string;
    required?: boolean;
  };
};

const Checkbox = ({
  className,
  checked,
  inline,
  onChange,
  inputAttrs,
  disabled = false,
  textAlignTop = false,
  children,
}: IProps) => (
  <CheckboxContainer
    className={className}
    onClick={() => !disabled && onChange(!checked)}
    $inline={inline}
    $disabled={disabled}
    $textAlignTop={textAlignTop}
  >
    <HiddenCheckbox
      required={inputAttrs?.required === true}
      title={inputAttrs?.title}
      ref={inputAttrs?.ref}
      checked={checked}
      disabled={disabled}
      onChange={event => onChange(event.target.checked)}
    />
    <StyledCheckbox
      checked={checked}
      disabled={disabled}
      $textAlignTop={textAlignTop}
    >
      <Icon viewBox="0 0 24 24">
        <polyline points="20 6 9 17 4 12" />
      </Icon>
    </StyledCheckbox>
    {children && <LabelWrap>{children}</LabelWrap>}
  </CheckboxContainer>
);

export default Checkbox;
